import { useState, useMemo } from "react";
import {
  Table,
  TableHead,
  TableHeadCell,
  TableBody,
  TableRow,
  TableCell,
  Button,
} from "flowbite-react";
import formatDate from "../../utils/formatDate";
import React from "react";

/* Import Heroicons for sort arrows */
import { HiArrowUp, HiArrowDown, HiFilter } from "react-icons/hi";

/* --------------------------------------------------------------- */
/*  SAFE date-string detector – never creates a Date from Symbol   */
/* --------------------------------------------------------------- */
const isDateString = (val) => {
  if (typeof val !== "string") return false;
  return /^\d{4}-\d{2}-\d{2}T/.test(val);
};

/* --------------------------------------------------------------- */
const DataTable = ({ data = [], excludeColumns = [], pages = true }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(50);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  /* ---------------------- Flatten nested rows ------------------- */
  const flattenData = useMemo(() => {
    return data.map((row) => {
      const flat = {};
      Object.entries(row).forEach(([k, v]) => {
        if (
          v &&
          typeof v === "object" &&
          !Array.isArray(v) &&
          !(v instanceof Date) &&
          !React.isValidElement(v)
        ) {
          Object.entries(v).forEach(([sk, sv]) => (flat[sk] = sv));
        } else {
          flat[k] = v;
        }
      });
      return flat;
    });
  }, [data]);

  /* ----------------------- Headers ----------------------------- */
  const headers = useMemo(() => {
    if (!flattenData.length) return [];
    const all = Object.keys(flattenData[0]);
    const filtered = all.filter((k) => !excludeColumns.includes(k));
    const actionsIdx = filtered.indexOf("actions");
    if (actionsIdx !== -1) {
      filtered.splice(actionsIdx, 1);
      filtered.push("actions"); // always last
    }
    return filtered;
  }, [flattenData, excludeColumns]);

  /* ----------------------- Sorting ----------------------------- */
  const handleSort = (key) => {
    if (key === "actions") return; // don't sort actions
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc")
      direction = "desc";
    setSortConfig({ key, direction });
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return flattenData;
    return [...flattenData].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [flattenData, sortConfig]);

  /* ---------------------- Pagination --------------------------- */
  const indexOfLast = currentPage * recordsPerPage;
  const indexOfFirst = indexOfLast - recordsPerPage;
  const currentRecords = sortedData.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(sortedData.length / recordsPerPage);
  const paginate = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  /* ----------------------- Helpers ----------------------------- */
  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) {
      return (
        <HiFilter className="w-4 h-4 inline ml-1 text-gray-400 dark:text-gray-500" />
      );
    }
    return sortConfig.direction === "asc" ? (
      <HiArrowUp className="w-4 h-4 inline ml-1 text-blue-600" />
    ) : (
      <HiArrowDown className="w-4 h-4 inline ml-1 text-blue-600" />
    );
  };

  const isImageUrl = (url) =>
    typeof url === "string" && /\.(jpeg|jpg|gif|png|webp)$/i.test(url);

  const formatHeader = (h) =>
    h ? h.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()) : "";

  const nameCol = headers.find((h) => h.toLowerCase() === "name");
  let imageCol;
  for (const h of headers) {
    if (flattenData.some((r) => isImageUrl(r[h]))) {
      imageCol = h;
      break;
    }
  }

  /* --------------------- Render Cell Value --------------------- *///eslint-disable-next-line no-unused-vars
  const renderCell = (value, key) => {
    // 1. React Element (e.g. <ButtonComponent ... />)
    if (React.isValidElement(value)) {
      return value;
    }

    // 2. Date string → format
    if (isDateString(value)) {
      return formatDate(value);
    }

    // 3. Plain value
    return value?.toString() ?? "-";
  };

  /* --------------------------------------------------------------- */
  return (
    <div className="overflow-x-auto">
      {headers.length === 0 ? (
        <div className="p-4 text-center text-gray-500 dark:text-gray-400">
          No data available
        </div>
      ) : (
        <>
          <Table hoverable className="dark:border-gray-700">
            <TableHead>
              <TableRow>
                {headers.map((key) => {
                  if (key === imageCol && nameCol) return null;
                  return (
                    <TableHeadCell
                      key={key}
                      className="cursor-pointer px-4 py-2 select-none bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-200"
                      onClick={() => handleSort(key)}
                    >
                      <div className="flex items-center">
                        {formatHeader(key)}
                        {key !== "actions" && getSortIndicator(key)}
                      </div>
                    </TableHeadCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {currentRecords.map((row, idx) => (
                <TableRow
                  key={idx}
                  className={
                    idx % 2 === 0
                      ? "bg-white dark:bg-gray-800"
                      : "bg-gray-50 dark:bg-gray-900"
                  }
                >
                  {headers.map((key) => {
                    /* ---- Merge image + name ---- */
                    if (key === nameCol && imageCol) {
                      return (
                        <TableCell
                          key={key}
                          className="whitespace-nowrap px-4 py-2 flex items-center gap-2"
                        >
                          <img
                            src={row[imageCol]}
                            alt="avatar"
                            className="w-10 h-10 object-cover rounded"
                          />
                          <span>{row[nameCol] ?? "-"}</span>
                        </TableCell>
                      );
                    }

                    if (key === imageCol && nameCol) return null;

                    /* ---- Actions column ---- */
                    if (key === "actions") {
                      return (
                        <TableCell
                          key={key}
                          className="whitespace-nowrap px-4 py-2"
                        >
                          {renderCell(row[key], key)}
                        </TableCell>
                      );
                    }

                    /* ---- Normal cell ---- */
                    return (
                      <TableCell
                        key={key}
                        className="whitespace-nowrap px-4 py-2 dark:text-gray-200"
                      >
                        {renderCell(row[key], key)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Pagination */}
          {pages && (
            <div className="flex justify-between items-center mt-4 space-x-2">
              <Button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                size="sm"
                className="bg-app-primary text-app-primary border-app-primary"
              >
                Previous
              </Button>

              <div className="flex space-x-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (p) => (
                    <Button
                      key={p}
                      className={`${
                        currentPage === p
                          ? "bg-app-primary text-app-primary"
                          : "bg-app-secondary text-app-secondary hover:bg-app-hover"
                      }`}
                      size="sm"
                      onClick={() => paginate(p)}
                    >
                      {p}
                    </Button>
                  )
                )}
              </div>

              <Button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                size="sm"
                className="bg-app-primary text-app-primary border-app-primary"
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DataTable;
